import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Card,
  SimpleGrid,
  Table,
  Badge,
  List,
  ThemeIcon,
  Divider,
  Box,
  Alert,
} from '@mantine/core';
import {
  IconBolt,
  IconCheck,
  IconX,
  IconCalculator,
  IconInfoCircle,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { Layout } from '../shared/components/Layout';
import { useSEO } from '../shared/hooks';
import { useOSD } from '../contexts/OSDContext';
import { getOSDMetadata, getDefaultOSD } from '../utils/osdMetadata';
import { getTariffsForOSD, type TariffDescription } from '../data/tariffs/tariffDescriptions';
import { TariffType } from '../types/graphql';

export function Tariffs() {
  const { currentOSD, getOSDPath } = useOSD();

  // Get OSD metadata for dynamic content
  const osd = currentOSD || getDefaultOSD();
  const osdMetadata = getOSDMetadata(osd);
  const availableTariffs = getTariffsForOSD(osd);
  const tariffCount = availableTariffs.length;

  useSEO({
    title: `Taryfy energetyczne 2026 - ${osdMetadata.shortName}`,
    description: `Porównaj ${tariffCount} taryf energetycznych ${osdMetadata.shortName}: G11, G12, G12w i więcej. Szczegółowe opisy, ceny i strefy czasowe.`,
  });

  // Helper function to get short description for table
  const getShortBestFor = (tariff: TariffDescription): string => {
    return tariff.bestFor[0] || tariff.shortDescription;
  };

  // Helper function to get time zone summary for table
  const getTarifTimeZoneShort = (tariff: TariffDescription): string => {
    switch (tariff.code) {
      case TariffType.G11:
        return '-';
      case TariffType.G12:
        return 'Noc (22:00-6:00)';
      case TariffType.G12w:
        return 'Noc + weekendy + święta';
      case TariffType.G12as:
        return 'Noc (22:00-6:00) - ogrzewanie elektryczne';
      case TariffType.G12n:
        return 'Noc (01:00-05:00) + weekendy (PGE)';
      case TariffType.G12r:
        return 'Noc (21:00-07:00) + weekendy (Energa)';
      case TariffType.G13:
        return 'Poza szczytem + noc';
      case TariffType.G13s:
        return 'Godziny słoneczne (9-17 lato)';
      case TariffType.G14:
        return 'Dynamiczne ceny godzinowe (RCE)';
      default:
        return '-';
    }
  };

  // Helper to get tariff type description
  const getTariffType = (tariff: TariffDescription): string => {
    switch (tariff.code) {
      case TariffType.G11:
        return 'Jednostrefowa';
      case TariffType.G12:
      case TariffType.G12as:
      case TariffType.G12n:
      case TariffType.G12r:
        return 'Dwustrefowa';
      case TariffType.G12w:
        return 'Dwustrefowa weekendowa';
      case TariffType.G13:
      case TariffType.G13s:
        return 'Trzystrefowa';
      case TariffType.G14:
        return 'Dynamiczna godzinowa';
      default:
        return '';
    }
  };

  return (
    <Layout isAuthenticated={false}>
      {/* Hero Section */}
      <Box style={{ backgroundColor: 'var(--mantine-color-blue-0)', padding: '3rem 0' }}>
        <Container size="xl">
          <Stack gap="md" align="center" ta="center">
            <ThemeIcon size={80} radius="xl" variant="light" color="blue">
              <IconBolt size={48} />
            </ThemeIcon>
            <Title order={1} size={42} fw={700}>
              Taryfy energetyczne 2026
            </Title>
            <Text size="lg" c="dimmed" maw={800}>
              Poznaj wszystkie {tariffCount} taryf energetycznych dostępnych w {osdMetadata.shortName} i wybierz najlepszą
              dla swojego gospodarstwa domowego. Szczegółowe informacje o cenach, strefach
              czasowych i opłatach stałych.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* What is "Seller Tariff" Explanation */}
      <Container size="xl" py={40}>
        <Alert icon={<IconInfoCircle />} color="blue" variant="light">
          <Stack gap="sm">
            <Text size="sm" fw={700}>
              Czym jest "taryfa sprzedawcy"?
            </Text>
            <Text size="sm" c="dimmed">
              Wszystkie ceny na tej stronie dotyczą <strong>taryfy sprzedawcy</strong>{' '}
              (pełny koszt energii elektrycznej). Każda podana stawka za kWh obejmuje:
            </Text>
            <Group gap="xl" mt="xs">
              <Stack gap="xs">
                <Text size="xs" fw={600}>
                  Składniki ceny (2026 - ceny rynkowe):
                </Text>
                <List size="xs" c="dimmed" spacing="xs">
                  <List.Item>
                    Cenę produkcji energii (~0,61 zł/kWh brutto - cena rynkowa URE 2026)
                  </List.Item>
                  <List.Item>
                    Opłaty dystrybucyjne (~0,52 zł/kWh - wzrost 9% vs 2025)
                  </List.Item>
                  <List.Item>Opłatę jakościową (0,0321 zł/kWh)</List.Item>
                  <List.Item>Opłaty stałe miesięczne (8,64 zł dla przyłącza jednofazowego)</List.Item>
                </List>
              </Stack>
            </Group>
            <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
              To kompletna cena, którą widzisz na rachunku od {osdMetadata.shortName} - nie musisz niczego
              dodawać.
            </Text>
            <Text size="xs" c="dimmed" mt="sm" fw={600}>
              📊 Źródło danych: Wszystkie ceny pochodzą z oficjalnych taryf zatwierdzonych przez
              Urząd Regulacji Energetyki (URE) oraz publicznie dostępnych taryf {osdMetadata.name},
              aktualne na styczeń 2026.
            </Text>
          </Stack>
        </Alert>
      </Container>

      {/* Quick Comparison Table */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Porównanie taryf - szybki przegląd
            </Title>
            <Text c="dimmed" mb="xl">
              Wszystkie {tariffCount} taryf {osdMetadata.shortName} w jednym miejscu. Zobacz, która pasuje do Twojego stylu życia.
            </Text>
          </div>

          <Table.ScrollContainer minWidth={800}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Taryfa</Table.Th>
                  <Table.Th>Typ</Table.Th>
                  <Table.Th>Tańsze godziny</Table.Th>
                  <Table.Th>Dla kogo</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {availableTariffs.map((tariff) => (
                  <Table.Tr key={tariff.code}>
                    <Table.Td>
                      <Group gap="xs">
                        <Badge color={tariff.color} size="lg" variant="filled">
                          {tariff.code}
                        </Badge>
                        {tariff.badge && (
                          <Badge color="gray" size="sm" variant="light">
                            {tariff.badge}
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>{getTariffType(tariff)}</Table.Td>
                    <Table.Td>{getTarifTimeZoneShort(tariff)}</Table.Td>
                    <Table.Td>{getShortBestFor(tariff)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Stack>
      </Container>

      {/* Detailed Tariff Descriptions */}
      <Box style={{ backgroundColor: 'var(--mantine-color-gray-0)' }} py={60}>
        <Container size="xl">
          <Stack gap="xl">
            <div>
              <Title order={2} mb="md">
                Szczegółowe opisy taryf {osdMetadata.shortName}
              </Title>
              <Text c="dimmed">
                Dowiedz się więcej o każdej z {tariffCount} taryf dostępnych w {osdMetadata.shortName},
                ich zaletach i wadach oraz dla kogo są najlepsze.
              </Text>
            </div>

            {availableTariffs.map((tariff) => (
              <Card key={tariff.code} shadow="sm" padding="xl" radius="md" withBorder>
                <Group justify="space-between" mb="md">
                  <Group>
                    <ThemeIcon size={50} radius="md" variant="light" color={tariff.color}>
                      <IconBolt size={28} />
                    </ThemeIcon>
                    <div>
                      <Group gap="xs" mb="xs">
                        <Badge color={tariff.color} size="lg" variant="filled">
                          {tariff.code}
                        </Badge>
                        {tariff.badge && (
                          <Badge color="gray" size="sm" variant="light">
                            {tariff.badge}
                          </Badge>
                        )}
                      </Group>
                      <Title order={3}>{tariff.name}</Title>
                    </div>
                  </Group>
                </Group>

                <Text size="lg" mb="lg">
                  {tariff.fullDescription}
                </Text>

                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                  <div>
                    <Text fw={600} mb="sm">
                      Dla kogo:
                    </Text>
                    <List
                      spacing="xs"
                      size="sm"
                      icon={
                        <ThemeIcon color={tariff.color} size={20} radius="xl">
                          <IconCheck size={12} />
                        </ThemeIcon>
                      }
                    >
                      {tariff.bestFor.slice(0, 4).map((item, index) => (
                        <List.Item key={index}>{item}</List.Item>
                      ))}
                    </List>
                  </div>

                  <div>
                    <Text fw={600} mb="sm" c="red">
                      Nie dla:
                    </Text>
                    <List
                      spacing="xs"
                      size="sm"
                      icon={
                        <ThemeIcon color="red" size={20} radius="xl">
                          <IconX size={12} />
                        </ThemeIcon>
                      }
                    >
                      {tariff.notFor.slice(0, 4).map((item, index) => (
                        <List.Item key={index}>{item}</List.Item>
                      ))}
                    </List>
                  </div>
                </SimpleGrid>

                <Divider my="lg" />

                <Group justify="space-between" align="flex-start">
                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      Potencjalne oszczędności:
                    </Text>
                    <Text size="lg" fw={700} c={tariff.color}>
                      {tariff.estimatedSavings}
                    </Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      Strefy czasowe:
                    </Text>
                    <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                      {tariff.timeZoneInfo}
                    </Text>
                  </div>
                </Group>

                {tariff.requiresSmartMeter && (
                  <Alert icon={<IconInfoCircle />} color={tariff.color} variant="light" mt="lg">
                    <Text size="sm">
                      <strong>Uwaga:</strong> Taryfa {tariff.code} wymaga inteligentnego licznika.
                      Skontaktuj się z {osdMetadata.shortName} w celu weryfikacji dostępności.
                    </Text>
                  </Alert>
                )}
              </Card>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Fixed Fees Section */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Opłaty stałe (2026)
            </Title>
            <Text c="dimmed" mb="lg">
              Niezależnie od wybranej taryfy, każde gospodarstwo domowe ponosi następujące
              miesięczne opłaty stałe:
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text size="sm" c="dimmed" mb="xs">
                Opłata stała (przyłącze jednofazowe)
              </Text>
              <Text size="xl" fw={700} c="blue">
                8,64 zł/miesiąc
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Opłata dystrybucyjna (abonament + dostęp do sieci)
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text size="sm" c="dimmed" mb="xs">
                Opłata stała (przyłącze trójfazowe)
              </Text>
              <Text size="xl" fw={700} c="blue">
                12,72 zł/miesiąc
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Dla instalacji z wyższą mocą przyłączeniową
              </Text>
            </Card>
          </SimpleGrid>

          <Card shadow="md" padding="lg" radius="md" style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
            <Group justify="space-between" align="center">
              <div>
                <Text size="sm" c="dimmed" mb="xs">
                  Typowa opłata stała miesięcznie:
                </Text>
                <Text size="2rem" fw={700} c="blue">
                  8,64 zł
                </Text>
                <Text size="xs" c="dimmed" mt="xs">
                  (dla przyłącza jednofazowego)
                </Text>
              </div>
              <div>
                <Text size="sm" c="dimmed" mb="xs">
                  Rocznie:
                </Text>
                <Text size="xl" fw={700} c="blue">
                  103,68 zł
                </Text>
                <Text size="xs" c="dimmed" mt="xs">
                  (8,64 zł × 12 miesięcy)
                </Text>
              </div>
            </Group>
          </Card>

          <Alert icon={<IconInfoCircle />} color="blue" variant="light">
            <Text size="sm">
              <strong>Dodatkowe informacje o opłatach (2026):</strong>
            </Text>
            <List size="sm" mt="xs">
              <List.Item>
                <strong>Cena energii elektrycznej:</strong> Od 1.01.2026 obowiązują ceny rynkowe
                - około 0,61 zł/kWh (brutto) zamiast zamrożonej ceny 0,62 zł/kWh
              </List.Item>
              <List.Item>
                <strong>Opłaty dystrybucyjne:</strong> Wzrost o ~9% względem 2025 roku
              </List.Item>
              <List.Item>
                <strong>Stawka jakościowa:</strong> 0,0321 zł/kWh - opłata za jakość energii
              </List.Item>
              <List.Item>
                <strong>Ceny zawierają VAT:</strong> Wszystkie podane ceny są cenami brutto
                (z VAT 23%)
              </List.Item>
            </List>
          </Alert>
        </Stack>
      </Container>

      {/* Price Disclaimer */}
      <Box style={{ backgroundColor: 'var(--mantine-color-green-0)' }} py={40}>
        <Container size="xl">
          <Stack gap="lg">
            <Alert icon={<IconInfoCircle />} color="green" variant="filled">
              <Title order={4} mb="sm" c="white">
                ℹ️ Ceny 2026 - koniec zamrożenia, nowe stawki rynkowe
              </Title>
              <Text size="sm" c="white" fw={600}>
                Od 1 stycznia 2026 roku obowiązują nowe ceny rynkowe energii elektrycznej.
                Zamrożenie cen (0,62 zł/kWh) zakończyło się 31.12.2025. Nowa cena energii wynosi
                około 0,61 zł/kWh brutto (-1,6%), ale opłaty dystrybucyjne wzrosły o ~9%.
                Łączny koszt G11 to około 1,13 zł/kWh brutto (+3% względem 2025).
              </Text>
            </Alert>

            <Alert icon={<IconInfoCircle />} color="orange" variant="light">
              <Title order={4} mb="sm">
                Ważne zastrzeżenia prawne
              </Title>
              <List size="sm" spacing="sm">
                <List.Item>
                  <strong>Źródło danych:</strong> Ceny pochodzą z taryf zatwierdzonych przez Urząd
                  Regulacji Energetyki (URE) oraz publicznie dostępnych taryf {osdMetadata.name}
                  (stan na styczeń 2026)
                </List.Item>
                <List.Item>
                  <strong>Aktualność danych:</strong> Ceny są aktualne na dzień publikacji, ale mogą
                  ulec zmianie w każdej chwili. Taryfy Prądu nie gwarantuje aktualności danych
                  cenowych i nie ponosi odpowiedzialności za ewentualne rozbieżności
                </List.Item>
                <List.Item>
                  <strong>Charakter informacyjny:</strong> Wszystkie kalkulacje mają charakter
                  orientacyjny i edukacyjny. Faktyczny koszt energii zależy od wielu czynników:
                  rzeczywistego zużycia, zmian cen przez dostawcę, opłat sieciowych, podatków oraz
                  indywidualnych warunków umowy
                </List.Item>
                <List.Item>
                  <strong>Zmienność cen:</strong> Ceny dystrybucji mogą się różnić w zależności od
                  Operatora Systemu Dystrybucyjnego (OSD). Podane ceny są dla obszaru {osdMetadata.shortName}
                  i mogą być inne u innych OSD
                </List.Item>
                <List.Item>
                  <strong>Weryfikacja wymagana:</strong> Przed podjęciem decyzji o zmianie taryfy
                  ZAWSZE sprawdź aktualne ceny bezpośrednio u swojego dostawcy energii i na stronie
                  URE (https://www.ure.gov.pl)
                </List.Item>
                <List.Item>
                  <strong>Brak odpowiedzialności:</strong> Taryfy Prądu nie ponosi odpowiedzialności
                  za decyzje podejmowane na podstawie informacji z tej strony ani za ewentualne
                  straty wynikające z niedokładności danych lub zmian cen
                </List.Item>
              </List>
            </Alert>

            <Alert icon={<IconInfoCircle />} color="blue" variant="light">
              <Title order={4} mb="sm">
                Dodatkowe informacje
              </Title>
              <List size="sm">
                <List.Item>
                  <strong>Ceny 2026:</strong> Od 1.01.2026 obowiązują ceny rynkowe URE (~0,61 zł/kWh
                  brutto za energię). Zamrożenie cen (0,62 zł/kWh) zakończyło się 31.12.2025
                </List.Item>
                <List.Item>
                  <strong>Składniki ceny:</strong> Podane ceny zawierają wszystkie składniki: cenę
                  produkcji energii, opłaty dystrybucyjne, opłatę jakościową oraz VAT 23%
                </List.Item>
                <List.Item>
                  <strong>Osobiste dopasowanie:</strong> Faktyczny rachunek zależy wyłącznie od
                  Twojego rzeczywistego zużycia energii i wybranej taryfy
                </List.Item>
                <List.Item>
                  <strong>Regularna weryfikacja:</strong> Zalecamy regularną weryfikację taryfy
                  (co 6-12 miesięcy) - Twój styl życia i wzorce zużycia mogą się zmienić
                </List.Item>
              </List>
            </Alert>
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box style={{ backgroundColor: 'var(--mantine-color-blue-6)', color: 'white' }} py={60}>
        <Container size="md">
          <Stack gap="xl" align="center" ta="center">
            <ThemeIcon size={80} radius="xl" variant="white" color="blue">
              <IconCalculator size={48} />
            </ThemeIcon>
            <Title order={2} c="white">
              Która taryfa jest dla Ciebie najlepsza?
            </Title>
            <Text size="lg" c="white" maw={700}>
              Użyj naszego kalkulatora, aby porównać koszty wszystkich taryf na podstawie
              Twojego rzeczywistego zużycia energii. Wprowadź swoje dane i dowiedz się, ile
              możesz zaoszczędzić!
            </Text>
            <Button
              size="xl"
              variant="white"
              color="blue"
              component={Link}
              to={getOSDPath('/kalkulatory/podstawowy')}
              leftSection={<IconCalculator size={24} />}
            >
              Przejdź do kalkulatora
            </Button>
          </Stack>
        </Container>
      </Box>
    </Layout>
  );
}
